/**
 * AddProblemEditor — inline Add/Edit form for the Problems chart tab
 * (PROD-827 chart write surfaces).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/problems_tab.dart`. Form
 * matches the Active-problem editor at L440-615 (Problem typeahead +
 * Onset Date + Severity dropdown + Description). The Resolve flow is
 * a SEPARATE confirm-dialog surface in the chart-tab wrapper that
 * picks an End Date and dispatches the same PUT with
 * `clinicalStatus: "Resolved"`.
 *
 * **Two modes, same form:**
 * - **Add** (`editing` omitted) → POST `/api/Patients/CreateCondition`
 *   with `CreateConditionBody`. Client-set `createdDate`,
 *   `asserter = userId`, slim `performerDetails`.
 * - **Edit** (`editing` is a `PatientCondition`) → PUT
 *   `/api/Patients/condition/{id}` with `UpdateConditionBody`.
 *   Round-trips `asserter`, `performerDetails`, `createdDate`,
 *   `speciality`, `bodySite` from the original record.
 *
 * Wire-shape gotchas (curl-locked):
 * - `code.Definition` PascalCase (capital D!)
 * - `bodySite` is an empty Coding on Create — `{system:null, code:null,
 *   display:null}`, NOT `null`
 * - `severity` is a plain string, not a Coding
 * - `asserter` is the current user's GUID on Create (not a separate
 *   `recorder` field like Allergies)
 */

import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button, FieldLabel, Select } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useDiagnosisCodeSearch } from '@/patients/services/use-diagnosis-code-search';
import { useCreateCondition } from '@/patients/services/use-create-condition';
import { useUpdateCondition } from '@/patients/services/use-update-condition';
import {
  normalizeCoding,
  normalizePerformerDetails,
} from './add-allergy-dialog';
import { DynamicDateInput } from './dynamic-date-input';
import { UNKNOWN_DATE_TEXT } from '@/encounter/lib/reported-date';
import {
  type ConditionCode,
  type CreateConditionBody,
  type PatientCondition,
  type UpdateConditionBody,
} from '@/types/patient-chart-api';
import {
  clientCreatedDate,
  emptyBodySite,
  normalizeConditionCode,
} from '@/patients/lib/conditions';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';

// ──────────────────────────────────────────────────────────────────────────
// Zod schema
// ──────────────────────────────────────────────────────────────────────────

/** Severity options — bare strings, NOT Codings (curl-verified). */
const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe', 'n/a'] as const;

const schema = z
  .object({
    /** Selected problem — stringified `ConditionCode`. */
    problemJson: z.string().min(1, 'Pick a problem'),
    severity: z.enum(SEVERITY_OPTIONS),
    /** User's free-text input — preserved verbatim and shipped to the
     *  backend as `onsetDisplayDateTime`. */
    onsetDisplay: z.string().min(1, 'Onset date is required').max(50),
    /** ISO resolved by `DynamicDateInput`'s parser. Null when the
     *  free-text didn't parse — the refine below blocks submit. */
    onsetIso: z.string().nullable(),
    note: z.string().max(500).optional(),
  })
  // Block submit when the dynamic-date free-text didn't resolve to a
  // valid ISO. Flutter ships a resolved ISO whenever the user types,
  // and the backend's `onset` field has no curl evidence for null
  // acceptance (same rationale as Allergies — Samer PR #214 review).
  //
  // ONE EXCEPTION: an explicit "Unknown" onset, recorded from the encounter rail as
  // `onsetDisplayDateTime: "Unknown"` with no timestamp (encounter owner answers on PR #843,
  // 1 Nov 2026; Andrew confirmed the slot). The wire takes it: Scrips.Patient
  // `CreatePatientConditionRequest.OnsetDateTime` is `DateTime?`. Without this a record the rail
  // saved as Unknown could not take even a note-only edit here.
  .refine((v) => v.onsetIso !== null || v.onsetDisplay.trim() === UNKNOWN_DATE_TEXT, {
    message:
      'Onset date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
    path: ['onsetDisplay'],
  });

type FormValues = z.infer<typeof schema>;

// ──────────────────────────────────────────────────────────────────────────
// Inline editor
// ──────────────────────────────────────────────────────────────────────────

export interface AddProblemEditorProps {
  patientId: string;
  onClose: () => void;
  /** Existing condition to edit. When present, submit goes to PUT. */
  editing?: PatientCondition | null;
}

export function AddProblemEditor({
  patientId,
  onClose,
  editing = null,
}: AddProblemEditorProps) {
  const isEditing = !!editing?.identifier;

  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';
  const userName = computeUserName(user);

  const initialValues = useMemo<FormValues>(() => {
    if (!editing) {
      return {
        problemJson: '',
        severity: 'n/a',
        onsetDisplay: '',
        onsetIso: null,
        note: '',
      };
    }
    return {
      problemJson: editing.code ? JSON.stringify(editing.code) : '',
      severity: pickSeverity(editing.severity),
      onsetDisplay: editing.onsetDisplayDateTime ?? '',
      onsetIso: editing.onsetDateTime ?? null,
      note: editing.note ?? '',
    };
  }, [editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const createMut = useCreateCondition();
  const updateMut = useUpdateCondition();
  const isPending = createMut.isPending || updateMut.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }

    const problem = safeParse<ConditionCode>(values.problemJson);
    if (!problem) {
      toast.error('Form values are corrupted — please re-pick the problem.');
      return;
    }

    // Onset ISO already resolved by `DynamicDateInput`'s parser and
    // stored in form state. Refine guard above guarantees it's
    // non-null here, except for an explicit "Unknown" onset (null, by design).
    const resolvedOnset = values.onsetIso;

    try {
      if (isEditing && editing) {
        // Strict asserter preservation — block Edit if the original
        // record doesn't carry an asserter GUID rather than silently
        // shipping null or substituting the current user. The
        // backend's `asserter` is FHIR-side ref and meaningful for
        // audit; clobbering or nulling it would corrupt chart history.
        // Samer PR #225 review §2.
        if (!editing.asserter) {
          toast.error(
            "Cannot edit — this record is missing its original asserter. Please contact support.",
          );
          return;
        }
        // Edit / Resolve / Delete all share this PUT — Edit ships
        // `clinicalStatus` UNCHANGED from the original record. The
        // Resolve flow is a separate handler in the chart-tab wrapper
        // that flips clinicalStatus + populates endDate.
        const body: UpdateConditionBody = {
          identifier: editing.identifier!,
          // PRESERVE clinicalStatus — Edit doesn't transition between
          // Active/Resolved. The Resolve action is a separate flow.
          clinicalStatus: clinicalStatusForEdit(editing.clinicalStatus),
          code: normalizeConditionCode(problem),
          bodySite: emptyBodySite(editing.bodySite),
          subject: patientId,
          dataAbsentReason: 'n/a',
          encounter: editing.encounter ?? null,
          asserter: editing.asserter,
          endDisplayDate: editing.endDisplayDate ?? null,
          onsetDisplayDateTime: values.onsetDisplay,
          onsetDateTime: resolvedOnset,
          endDate: editing.endDate ?? null,
          note: values.note?.trim() || '',
          severity: values.severity,
          isDeleted: false,
          performerDetails: normalizePerformerDetails(editing.performerDetails),
          createdDate: editing.createdDate ?? null,
          // Top-level speciality IS populated on Conditions UPDATE
          // (different from Allergies which ships null here).
          speciality: normalizeCoding(editing.speciality),
        };
        await updateMut.mutateAsync({
          patientId,
          conditionId: editing.identifier!,
          body,
        });
        toast.success(
          `Updated problem: ${problem.display ?? problem.code ?? 'Unknown'}`,
        );
      } else {
        const body: CreateConditionBody = {
          identifier: null,
          clinicalStatus: 'Active',
          code: normalizeConditionCode(problem),
          // Empty Coding (NOT null) — backend rejects null bodySite on
          // Create. `emptyBodySite(null)` returns a fresh copy of the
          // frozen `EMPTY_BODY_SITE` sentinel, so we can't accidentally
          // mutate the constant.
          bodySite: emptyBodySite(null),
          subject: patientId,
          dataAbsentReason: 'n/a',
          encounter: null,
          // Asserter = current user's GUID on Create (Conditions uses
          // `asserter` for the recording role; Allergies uses `recorder`).
          asserter: userId,
          endDisplayDate: null,
          onsetDisplayDateTime: values.onsetDisplay,
          onsetDateTime: resolvedOnset,
          endDate: null,
          note: values.note?.trim() || '',
          severity: values.severity,
          isDeleted: false,
          performerDetails: {
            id: null,
            name: userName,
            photo: null,
            speciality: {
              specialitySkillId: '00000000-0000-0000-0000-000000000000',
              code: null,
            },
            timeZone: null,
            organizationId: null,
            roleName: null,
          },
          // ⚠️ CLIENT-SET timestamp — Samer PR #225 review §1 flagged
          //  this as a PHI integrity risk. Device clock skew or
          //  timezone drift here would corrupt the timestamp on the
          //  wire. Curl observation suggests the backend OVERWRITES
          //  this on persist (UPDATE response preserved server time,
          //  not client time) — but the field can't be omitted
          //  (backend rejects missing key with 400). Backend
          //  alignment tracked at PROD-837 — flag there if you see
          //  drift between shipped and stored values. See
          //  `clientCreatedDate()` + `CreateConditionBody` docblocks.
          createdDate: clientCreatedDate(),
          speciality: null,
        };
        await createMut.mutateAsync({ patientId, body });
        toast.success(
          `Added problem: ${problem.display ?? problem.code ?? 'Unknown'}`,
        );
      }
      form.reset();
      onClose();
    } catch {
      /* Global error toast surfaces failure; keep editor open. */
    }
  });

  return (
    // The design's PaEditorCard: the one card (hairline, 12 corners, white, no shadow, 24 padding); look in
    // showcase-patient.css `editor card`.
    <div data-slot="editor-card">
      <form onSubmit={submit} className="space-y-6">
        <Controller
          name="problemJson"
          control={form.control}
          render={({ field, fieldState }) => (
            <ProblemPicker
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            name="severity"
            control={form.control}
            render={({ field, fieldState }) => (
              <div>
                <FieldLabel
                  htmlFor="severity"
                  
                >
                  Severity
                </FieldLabel>
                <Select
                  id="severity"
                  className="mt-1"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select severity"
                  options={SEVERITY_OPTIONS.map((s) => ({ label: s, value: s }))}
                />
                {fieldState.error ? (
                  <p
                    className="text-xs text-red-700 mt-1"
                    role="alert"
                  >
                    {fieldState.error.message}
                  </p>
                ) : null}
              </div>
            )}
          />

          <Controller
            name="onsetIso"
            control={form.control}
            render={({ field: isoField }) => (
              <Controller
                name="onsetDisplay"
                control={form.control}
                render={({ field: displayField, fieldState }) => (
                  <DynamicDateInput
                    label="Onset date"
                    required
                    displayValue={displayField.value}
                    isoValue={isoField.value}
                    onChange={(next) => {
                      displayField.onChange(next.displayValue);
                      isoField.onChange(next.isoValue);
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </div>

        <div>
          <FieldLabel
            htmlFor="note"
            
          >
            Problem description
          </FieldLabel>
          <Textarea
            id="note"
            {...form.register('note')}
            placeholder="Optional context, e.g. severity progression, related labs…"
            rows={3}
            className="mt-1"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                Saving…
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Done'
            )}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ProblemPicker — DiagnosisCodes typeahead
// ──────────────────────────────────────────────────────────────────────────

function ProblemPicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const searchQ = useDiagnosisCodeSearch(debounced, true);

  const selected = useMemo<ConditionCode | null>(
    () => (value ? safeParse<ConditionCode>(value) : null),
    [value],
  );

  const results = searchQ.data ?? [];

  return (
    <div>
      <FieldLabel
        htmlFor="problem"
        
      >
        Problem <span className="text-red-700">*</span>
      </FieldLabel>

      {selected ? (
        (() => {
          // The selected value has ALREADY been flattened at
          // selection time (see `flattenMasterCodeRow` below), so we
          // can read fields directly. Stay defensive anyway in case a
          // legacy stored value still carries the wrapped shape.
          const flat = flattenMasterCodeRow(selected);
          const selectedCode = flat.code ?? '';
          const selectedDisplay = flat.display?.trim() || '';
          return (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
              <div className="min-w-0">
                <p className={v2Look('text-sm font-medium text-brand-depth truncate', V2_BODY_EMPHASIS)}>
                  {selectedDisplay || selectedCode || '—'}
                </p>
                {selectedCode ? (
                  <p className="text-xs text-text-hint">
                    {selectedCode}
                    {flat.Definition ? ` · ${flat.Definition}` : ''}
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onChange('');
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              id="problem"
              type="text"
              placeholder="Type to search problems (e.g. migraine, R10.84)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          {debounced.trim().length >= 2 ? (
            <div className="mt-1 max-h-56 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
              {searchQ.isFetching ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  No matches. Try another word or a code.
                </div>
              ) : (
                results.map((row, idx) => {
                  // The master endpoint returns rows as
                  // `{code: ActualCoding}` wrappers — the master row's
                  // `code` field holds the real Coding (with PascalCase
                  // System + Definition). Flatten on read so render
                  // and selection both use the unwrapped shape.
                  const flat = flattenMasterCodeRow(row);
                  const codeStr = flat.code ?? '';
                  const displayStr = flat.display?.trim() || '';
                  const rowKey = `${codeStr}-${displayStr}-${idx}`;
                  return (
                    <button
                      key={rowKey}
                      type="button"
                      onClick={() => {
                        // Store the FLAT shape (matches the wire
                        // body's `code` field). The submit handler
                        // re-runs this through `normalizeConditionCode`
                        // to ensure all 6 wire fields are present-with-
                        // null.
                        onChange(JSON.stringify(flat));
                        setQuery('');
                      }}
                      className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                    >
                      <span className="text-sm text-text-primary block">
                        {displayStr || codeStr || '—'}
                      </span>
                      <span className="text-xs text-text-hint">
                        {codeStr}
                        {flat.Definition ? ` · ${flat.Definition}` : ''}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/** Edit preserves the existing clinicalStatus — the Resolve flow is a
 *  separate handler that explicitly flips to "Resolved". Conservative
 *  fallback: if the record has no clinicalStatus, treat as Active. */
function clinicalStatusForEdit(
  status: string | null | undefined,
): 'Active' | 'Resolved' {
  return status === 'Resolved' ? 'Resolved' : 'Active';
}

function pickSeverity(s: string | null | undefined): (typeof SEVERITY_OPTIONS)[number] {
  if (!s) return 'n/a';
  if ((SEVERITY_OPTIONS as readonly string[]).includes(s)) {
    return s as (typeof SEVERITY_OPTIONS)[number];
  }
  return 'n/a';
}

function computeUserName(
  user: { firstName?: string; lastName?: string } | null,
): string | null {
  if (!user) return null;
  const parts = [user.firstName, user.lastName]
    .map((s) => s?.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Flatten a row from `/api/Master/DiagnosisCodes` to the wire-body
 * `code` shape (the 6-field flat ConditionCode).
 *
 * **Master-response reality** (Andrew browser 400 2026-05-20): the
 * endpoint returns rows as `{ code: ActualCoding }` wrappers — the
 * row's `code` field IS the real Coding (with PascalCase `System` +
 * `Definition`), NOT a string. When the picker stored the raw row
 * verbatim, `JSON.stringify(row)` produced
 * `{"code":{nested coding}}`, which the create handler then nested
 * AGAIN under its own `code` field, shipping `code.code: <object>`
 * to the backend. Backend rejected with 400 ("Unexpected character
 * encountered while parsing value: {").
 *
 * Resolution: at selection time, detect the wrapped shape and lift
 * the nested Coding's fields up. Also coalesce PascalCase `System`
 * to lowercase `system` so the wire matches Flutter's
 * `ConditionCode` shape. Backwards-compatible with the already-flat
 * shape (idempotent on flat input).
 *
 * Returns the strict ConditionCode shape — Definition stays PascalCase
 * (wire literal), system is lowercase, never returns an object inside
 * `code`.
 */
function flattenMasterCodeRow(row: unknown): ConditionCode {
  if (!row || typeof row !== 'object') {
    return {
      code: null,
      Definition: null,
      display: null,
      system: null,
      description: null,
      shortName: null,
    };
  }
  const r = row as Record<string, unknown>;
  // If `r.code` is itself an object, the wrapped shape — lift fields
  // from the inner Coding instead of the outer wrapper.
  const inner =
    r.code && typeof r.code === 'object'
      ? (r.code as Record<string, unknown>)
      : r;
  return {
    code: typeof inner.code === 'string' ? inner.code : null,
    Definition:
      typeof inner.Definition === 'string'
        ? inner.Definition
        : typeof inner.definition === 'string'
          ? inner.definition
          : null,
    display: typeof inner.display === 'string' ? inner.display : null,
    // Master ships PascalCase `System`; the wire body expects lowercase
    // `system`. Coalesce so the picker stores the right key.
    system:
      typeof inner.system === 'string'
        ? inner.system
        : typeof inner.System === 'string'
          ? inner.System
          : null,
    description:
      typeof inner.description === 'string' ? inner.description : null,
    shortName:
      typeof inner.shortName === 'string' ? inner.shortName : null,
  };
}
